import { useEffect, useRef, useState } from "react";

type UseFriendlyLoadingStateOptions = {
  grace?: number; // How long to wait before showing the loader (ms)
  dwell?: number; // How long to keep the loader visible once shown (ms)
};

type LoadingState = 'idle' | 'loading' | 'loaded';

/**
 * A user-friendly wrapper around a boolean `loading` state that provides 3 states:
 * - 'idle': Initial state, shows nothing
 * - 'loading': Shows loader after grace period
 * - 'loaded': Shows content, respects dwell time if loader was shown
 */
export function useFriendlyLoadingState(
  loading: boolean,
  options: UseFriendlyLoadingStateOptions = {}
): LoadingState {
  const grace = options.grace ?? 200;
  const dwell = options.dwell ?? 300;

  // Initialize state based on initial loading value
  const [state, setState] = useState<LoadingState>(loading ? 'idle' : 'loaded');
  const graceTimeout = useRef<NodeJS.Timeout | null>(null);
  const dwellTimeout = useRef<NodeJS.Timeout | null>(null);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      // If we're in loaded state and loading starts, reset to idle for new cycle
      if (state === 'loaded') {
        setState('idle');
      }
      
      // Clear any pending dwell timeout
      if (dwellTimeout.current !== null) {
        clearTimeout(dwellTimeout.current);
        dwellTimeout.current = null;
      }

      // Only start grace period if we're in idle state and no grace timer is running
      if (state === 'idle' && graceTimeout.current === null) {
        graceTimeout.current = setTimeout(() => {
          shownAt.current = performance.now();
          setState('loading');
          graceTimeout.current = null;
        }, grace);
      }
    } else {
      // Only transition to loaded if we're in a loading cycle (idle or loading)
      if (state === 'idle' || state === 'loading') {
        // Clear grace timer if still pending
        if (graceTimeout.current !== null) {
          clearTimeout(graceTimeout.current);
          graceTimeout.current = null;
        }

        // Handle transition to loaded state
        setState((currentState) => {
          if (currentState === 'loading' && shownAt.current !== null) {
            // Enforce dwell time since loader was shown
            const elapsed = performance.now() - shownAt.current;
            const waitTime = Math.max(0, dwell - elapsed);

            dwellTimeout.current = setTimeout(() => {
              setState('loaded');
              shownAt.current = null;
              dwellTimeout.current = null;
            }, waitTime);
            
            return currentState; // Stay in loading state during dwell
          } else {
            // Go directly to loaded (grace period saved us, or we were already loaded)
            shownAt.current = null;
            return 'loaded';
          }
        });
      }
    }

    return () => {
      if (graceTimeout.current !== null) {
        clearTimeout(graceTimeout.current);
        graceTimeout.current = null;
      }
      if (dwellTimeout.current !== null) {
        clearTimeout(dwellTimeout.current);
        dwellTimeout.current = null;
      }
    };
  }, [loading, grace, dwell, state]);

  return state;
}